import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ALL_CATEGORIES,
  ALL_ENABLED,
  DEFAULT_PREFERENCES,
  type CookiePreferences,
  readConsent,
  saveConsent,
} from "@/lib/cookie-consent";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CookiePreferencesDialog({ open, onClose }: Props) {
  const [prefs, setPrefs] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    if (!open) return;
    const existing = readConsent();
    setPrefs(existing?.preferences ?? DEFAULT_PREFERENCES);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const toggle = (key: keyof CookiePreferences) => {
    if (key === "necessary") return;
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const persist = (next: CookiePreferences) => {
    saveConsent(next);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie preferences"
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 px-4 py-6 md:items-center"
    >
      <div className="w-full max-w-2xl border border-border bg-background text-foreground shadow-2xl">
        <div className="border-b border-border px-6 py-5">
          <p className="text-[10px] tracking-wide-luxury text-accent">
            Your Choices
          </p>
          <h2 className="mt-2 font-display text-2xl">Cookie preferences</h2>
          <p className="mt-2 text-xs text-muted-foreground">
            Choose which categories you allow. You can change this at any time
            from the link in the footer. See our{" "}
            <Link to="/legal/cookies" className="underline hover:text-accent">
              Cookie Policy
            </Link>
            .
          </p>
        </div>

        <div className="max-h-[55vh] overflow-y-auto px-6 py-4">
          <ul className="divide-y divide-border">
            {ALL_CATEGORIES.map((cat) => {
              const enabled = prefs[cat.key] || !!cat.required;
              return (
                <li
                  key={cat.key}
                  className="flex items-start justify-between gap-4 py-4"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {cat.label}
                      {cat.required && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide-luxury text-muted-foreground/70">
                          Always on
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {cat.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`Toggle ${cat.label}`}
                    disabled={cat.required}
                    onClick={() => toggle(cat.key)}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                      enabled ? "bg-accent" : "bg-muted"
                    } ${cat.required ? "opacity-60" : ""}`}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                        enabled ? "translate-x-[22px]" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={() => persist(DEFAULT_PREFERENCES)}
            className="border border-border px-5 py-2 text-[10px] tracking-wide-luxury text-muted-foreground transition-colors hover:border-accent hover:text-accent"
          >
            Reject all
          </button>
          <button
            type="button"
            onClick={() => persist(prefs)}
            className="border border-border px-5 py-2 text-[10px] tracking-wide-luxury text-muted-foreground transition-colors hover:border-accent hover:text-accent"
          >
            Save preferences
          </button>
          <button
            type="button"
            onClick={() => persist(ALL_ENABLED)}
            className="bg-accent px-5 py-2 text-[10px] tracking-wide-luxury text-primary-foreground transition-colors hover:bg-accent/90"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
